/**
 * Analytics tracker — thin wrapper over window.gtag.
 *
 * gtag itself is loaded once by `public/index.html` (hardcoded measurement ID)
 * and initialised with Google Consent Mode v2 by `components/AnalyticsProvider`.
 * This module ONLY dispatches events; it does not load scripts or manage consent.
 *
 * gtag internally honours Consent Mode: with `analytics_storage: 'denied'` events
 * still go out as cookieless aggregated pings, so we can call these helpers
 * unconditionally. When users accept the cookie banner, gtag automatically
 * upgrades to full tracking.
 */
import logger from '../lib/logger';

const gtagAvailable = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

class AnalyticsService {
  init(/* settings */) {
    // Kept as a no-op for backwards compatibility with older imports.
    // Boot is now handled by <AnalyticsProvider>. This method is intentionally empty.
  }

  trackPageView(path, title) {
    if (!gtagAvailable()) return;
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href,
    });
  }

  trackEvent(eventName, params = {}) {
    if (!gtagAvailable()) return;
    try {
      window.gtag('event', eventName, params);
    } catch (e) {
      logger.error('gtag event error', e);
    }
  }

  // ---- GA4 recommended e-commerce events (mapped to Enhanced Measurement) ----

  trackViewItem(product) {
    if (!product) return;
    this.trackEvent('view_item', {
      currency: 'USD',
      value: Number(product.price) || 0,
      items: [{
        item_id: product.id || product.slug,
        item_name: product.name,
        item_category: product.category || product.category_slug,
        price: Number(product.price) || 0,
        quantity: 1,
      }],
    });
  }

  trackAddToCart(product, quantity = 1) {
    if (!product) return;
    this.trackEvent('add_to_cart', {
      currency: 'USD',
      value: (Number(product.price) || 0) * quantity,
      items: [{
        item_id: product.id || product.slug,
        item_name: product.name,
        price: Number(product.price) || 0,
        quantity,
      }],
    });
  }

  trackBeginCheckout(cartItems = [], total = 0) {
    this.trackEvent('begin_checkout', {
      currency: 'USD',
      value: Number(total) || 0,
      items: (cartItems || []).map((item) => ({
        item_id: item.id || item.slug,
        item_name: item.name,
        price: Number(item.price) || 0,
        quantity: item.quantity || 1,
      })),
    });
  }

  trackPurchase(orderId, total, items = []) {
    this.trackEvent('purchase', {
      transaction_id: orderId,
      currency: 'USD',
      value: Number(total) || 0,
      items: (items || []).map((item) => ({
        item_id: item.id || item.slug || item.product_id,
        item_name: item.name || item.product_name,
        price: Number(item.price) || 0,
        quantity: item.quantity || 1,
      })),
    });
  }

  // ---- Lead / conversion events ----

  trackGenerateLead(source, extras = {}) {
    // GA4 recommended `generate_lead` event — mark as a conversion in GA4 UI.
    this.trackEvent('generate_lead', {
      lead_source: source, // e.g. 'newsletter_footer', 'lead_magnet_home', 'contact_form'
      currency: 'USD',
      value: 0,
      ...extras,
    });
  }

  trackContact(formName = 'contact_page') {
    this.trackEvent('contact', { form_name: formName });
  }

  trackDownload(fileName, source = 'storefront') {
    this.trackEvent('file_download', {
      file_name: fileName,
      link_url: fileName,
      lead_source: source,
    });
  }

  trackSelectContent(contentType, itemId) {
    // Useful for CTA click tracking. E.g. select_content('hero_primary_cta', 'shop').
    this.trackEvent('select_content', {
      content_type: contentType,
      item_id: itemId,
    });
  }

  trackSearch(query) {
    this.trackEvent('search', { search_term: query });
  }

  trackSignUp(method = 'newsletter') {
    this.trackEvent('sign_up', { method });
  }
}

const analytics = new AnalyticsService();
export default analytics;
